<template>
  <div class="settings-container p-6">
    <h1 class="text-2xl font-semibold mb-6 text-gray-900 dark:text-gray-200">Organization Settings</h1>

    <div v-if="isLoading === true" class="text-center">
      <span class="loading loading-spinner loading-lg"></span>
    </div>

    <div v-else-if="loadingError !== null" class="alert alert-error">
      <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2 2m2-2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      <span>Error loading settings: {{ loadingError }}</span>
    </div>

    <div v-else class="card bg-base-100 shadow-xl w-full mx-auto border dark:border-gray-700">
      <div class="card-body">
        <form @submit.prevent="saveSettings">
          <div role="tablist" class="tabs tabs-lifted mb-6">
            <input type="radio" name="org_settings_tabs" role="tab" class="tab" aria-label="General" value="general" v-model="activeTab" />
            <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-6">
              <div class="form-control mb-8">
                <label class="label">
                  <span class="label-text font-medium">Organization Name</span>
                </label>
                <input 
                  type="text" 
                  v-model="settings.organizationName" 
                  placeholder="Enter organization name" 
                  class="input input-bordered w-full" 
                  required
                />
              </div>

              <div class="form-control mb-8">
                <label class="label">
                  <span class="label-text font-medium">Subscription</span>
                </label>
                <input
                  type="text"
                  :value="formatSubscriptionLabel(settings.subscription)"
                  class="input input-bordered w-full"
                  readonly
                />
              </div>

              <div class="form-control mb-8">
                <label class="label">
                  <span class="label-text font-medium">Time Zone</span>
                </label>
                <MultiSelect
                  v-model="settings.timezone"
                  :options="timezones"
                  idField="tzCode"
                  labelField="label"
                  placeholder="Search or select timezone"
                  class="multiselect-custom"
                  :maxSelections="1"
                />
                <div class="text-xs text-gray-500 mt-2">Affects how dates/times are displayed and potentially processed.</div>
              </div>

              <div class="form-control mb-8">
                <label class="label">
                  <span class="label-text font-medium">Organization Logo</span>
                </label>
                <div class="flex flex-col md:flex-row items-stretch">
                  <div class="flex-1 w-full">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml,image/gif"
                      class="file-input file-input-bordered w-full mb-4"
                      @change="onLogoFileChange"
                      ref="logoFileInput"
                    />
                    <input
                      type="text"
                      v-model="settings.logoUrl"
                      class="input input-bordered w-full"
                      placeholder="Enter logo URL or upload a file"
                      @blur="onLogoUrlBlur"
                    />
                  </div>
                  <div class="mt-4 md:mt-0 w-full md:w-48 shrink-0 flex flex-col items-center justify-center p-4 bg-base-200 border border-base-300 rounded-xl md:mx-4">
                    <img v-if="settings.logoUrl !== null && settings.logoUrl !== ''" :src="settings.logoUrl" alt="Logo preview" class="max-h-24 max-w-full object-contain rounded-md shadow-sm bg-white p-1" />
                    <div v-else class="text-gray-400 text-sm flex flex-col items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 mb-1 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      No Logo
                    </div>
                    <button
                      v-if="settings.logoUrl !== null && settings.logoUrl !== ''"
                      type="button"
                      class="btn btn-ghost btn-xs mt-3 text-error"
                      @click="clearLogo"
                      :disabled="isSaving === true"
                    >
                      Remove Logo
                    </button>
                  </div>
                </div>
              </div>

              <div class="form-control mb-8 hidden">
                <label class="label">
                  <span class="label-text font-medium">Default Language</span>
                </label>
                <select v-model="settings.language" class="select select-bordered w-full">
                  <option disabled value="">Select language</option>
                  <option value="en">English</option>
                  <option value="he">עברית (Hebrew)</option>
                </select>
                <div class="text-xs text-gray-500 mt-2">Sets the default language for UI elements where applicable.</div>
              </div>

              <div class="form-control mb-8">
                <label class="label">
                  <span class="label-text font-medium">Allowed IPs</span>
                </label>
                <div class="flex items-center mb-4">
                  <input 
                    type="text" 
                    v-model="newIp" 
                    @keyup.enter.prevent="addIp"
                    placeholder="e.g. 192.168.1.1" 
                    class="input input-bordered flex-1" 
                  />
                  <button type="button" @click="addIp" class="btn mx-4">Add</button>
                </div>
                <div v-if="ipList.length > 0" class="mt-4">
                  <div v-for="(ip, index) in ipList" :key="index" class="flex items-center justify-between bg-base-200 px-4 py-3 rounded-lg mb-3 border border-base-300">
                    <span class="font-mono text-sm">{{ ip }}</span>
                    <button type="button" @click="removeIp(index)" class="btn btn-ghost btn-sm text-error hover:bg-error hover:text-white transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </div>
                </div>
                <div v-else class="text-sm text-gray-500 italic mt-4 p-4 bg-base-200 rounded-lg border border-base-300 text-center">
                  No IP restrictions. All IPs are allowed.
                </div>
                <div class="text-xs text-gray-500 mt-2">Add specific IP addresses to restrict backoffice access.</div>
              </div>
            </div>

            <input type="radio" name="org_settings_tabs" role="tab" class="tab" aria-label="Custom Domains" value="domains" v-model="activeTab" />
            <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-6">
              <div class="mb-6 rounded-2xl border border-base-300 bg-base-200/50 p-5">
                <div class="text-base font-semibold text-gray-900 dark:text-gray-100">Custom Domains</div>
                <div class="mt-1 text-sm text-gray-600 dark:text-gray-400">These domains are used for frontend routing, open chat links and public entry points for this organization.</div>
              </div>

              <div class="-mx-2 flex flex-wrap">
                <div class="mb-4 w-full px-2 md:w-1/2 xl:w-1/3">
                  <div class="h-full rounded-2xl border border-base-300 bg-base-200/40 p-5">
                    <div class="flex items-center justify-between">
                      <div class="text-sm font-semibold text-gray-900 dark:text-gray-100">Backoffice UI</div>
                      <span class="rounded-full px-2 py-1 text-xs font-medium" :class="settings.customDomains.backofficeUi !== '' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'">{{ settings.customDomains.backofficeUi !== '' ? 'Configured' : 'Empty' }}</span>
                    </div>
                    <div class="mt-2 text-xs text-gray-500 dark:text-gray-400">Used for main backoffice UI links.</div>
                    <input type="url" v-model="settings.customDomains.backofficeUi" class="input input-bordered mt-4 w-full" placeholder="https://backoffice.example.com" />
                  </div>
                </div>

                <div class="mb-4 w-full px-2 md:w-1/2 xl:w-1/3">
                  <div class="h-full rounded-2xl border border-base-300 bg-base-200/40 p-5">
                    <div class="flex items-center justify-between">
                      <div class="text-sm font-semibold text-gray-900 dark:text-gray-100">API</div>
                      <span class="rounded-full px-2 py-1 text-xs font-medium" :class="settings.customDomains.api !== '' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'">{{ settings.customDomains.api !== '' ? 'Configured' : 'Empty' }}</span>
                    </div>
                    <div class="mt-2 text-xs text-gray-500 dark:text-gray-400">Used for API requests and service endpoints.</div>
                    <input type="url" v-model="settings.customDomains.api" class="input input-bordered mt-4 w-full" placeholder="https://api.example.com" />
                  </div>
                </div>

                <div class="mb-4 w-full px-2 md:w-1/2 xl:w-1/3">
                  <div class="h-full rounded-2xl border border-base-300 bg-base-200/40 p-5">
                    <div class="flex items-center justify-between">
                      <div class="text-sm font-semibold text-gray-900 dark:text-gray-100">Assistant</div>
                      <span class="rounded-full px-2 py-1 text-xs font-medium" :class="settings.customDomains.assistant !== '' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'">{{ settings.customDomains.assistant !== '' ? 'Configured' : 'Empty' }}</span>
                    </div>
                    <div class="mt-2 text-xs text-gray-500 dark:text-gray-400">Used for assistant login and assistant chat routes.</div>
                    <input type="url" v-model="settings.customDomains.assistant" class="input input-bordered mt-4 w-full" placeholder="https://assistant.example.com" />
                  </div>
                </div>

                <div class="mb-4 w-full px-2 md:w-1/2 xl:w-1/3">
                  <div class="h-full rounded-2xl border border-base-300 bg-base-200/40 p-5">
                    <div class="flex items-center justify-between">
                      <div class="text-sm font-semibold text-gray-900 dark:text-gray-100">Agentic IVR</div>
                      <span class="rounded-full px-2 py-1 text-xs font-medium" :class="settings.customDomains.agenticIvr !== '' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'">{{ settings.customDomains.agenticIvr !== '' ? 'Configured' : 'Empty' }}</span>
                    </div>
                    <div class="mt-2 text-xs text-gray-500 dark:text-gray-400">Used for Agentic IVR voice websocket and setup routes.</div>
                    <input type="url" v-model="settings.customDomains.agenticIvr" class="input input-bordered mt-4 w-full" placeholder="https://agenticivr.example.com" />
                  </div>
                </div>

                <div class="mb-4 w-full px-2 md:w-1/2 xl:w-1/3">
                  <div class="h-full rounded-2xl border border-base-300 bg-base-200/40 p-5">
                    <div class="flex items-center justify-between">
                      <div class="text-sm font-semibold text-gray-900 dark:text-gray-100">Agents</div>
                      <span class="rounded-full px-2 py-1 text-xs font-medium" :class="settings.customDomains.agents !== '' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'">{{ settings.customDomains.agents !== '' ? 'Configured' : 'Empty' }}</span>
                    </div>
                    <div class="mt-2 text-xs text-gray-500 dark:text-gray-400">Used by open chat for public agents.</div>
                    <input type="url" v-model="settings.customDomains.agents" class="input input-bordered mt-4 w-full" placeholder="https://agents.example.com" />
                  </div>
                </div>

                <div class="mb-4 w-full px-2 md:w-1/2 xl:w-1/3">
                  <div class="h-full rounded-2xl border border-base-300 bg-base-200/40 p-5">
                    <div class="flex items-center justify-between">
                      <div class="text-sm font-semibold text-gray-900 dark:text-gray-100">Vendor Backoffice</div>
                      <span class="rounded-full px-2 py-1 text-xs font-medium" :class="settings.customDomains.vendorBackoffice !== '' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'">{{ settings.customDomains.vendorBackoffice !== '' ? 'Configured' : 'Empty' }}</span>
                    </div>
                    <div class="mt-2 text-xs text-gray-500 dark:text-gray-400">Used for Vendor Backoffice entry links.</div>
                    <input type="url" v-model="settings.customDomains.vendorBackoffice" class="input input-bordered mt-4 w-full" placeholder="https://vendor.example.com" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="card-actions justify-end mt-8">
            <button 
              type="submit" 
              class="btn"
              :disabled="isSaving === true"
            >
              <span v-if="isSaving === true" class="loading loading-spinner loading-xs mr-2"></span>
              {{ isSaving === true ? 'Saving...' : 'Save Settings' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from '../api/axiosInstance';
import { globalState, updateWhitelabelLogo } from '@/globalState';
import { useToast } from 'vue-toastification';
import timezones from 'timezones-list';
import MultiSelect from '@/components/MultiSelect.vue';
import { isDefinedAndNotEmpty } from '../../isDefinedAndNotEmpty.js';
import { getLogger } from '@/shared/tools/logService.js';

const log = getLogger('OrganizationSettings.vue');
const apiEndpoint = globalState.apiEndpoint;
const toast = useToast();

const settings = ref({
  organizationName: '',
  subscription: '',
  subscriptionConfig: null,
  timezone: [],
  language: 'en',
  logoUrl: '',
  allowedIps: '',
  customDomains: {
    backofficeUi: '',
    api: '',
    assistant: '',
    agenticIvr: '',
    agents: '',
    vendorBackoffice: ''
  }
});
const ipList = ref([]);
const newIp = ref('');
const isLoading = ref(true);
const isSaving = ref(false);
const loadingError = ref(null);
const logoFileInput = ref(null);
const activeTab = ref('general');

const normalizeCustomDomains = (value) => {
  const input = typeof value === 'object' && value !== null ? value : {};
  return {
    backofficeUi: typeof input.backofficeUi === 'string' ? input.backofficeUi : '',
    api: typeof input.api === 'string' ? input.api : '',
    assistant: typeof input.assistant === 'string' ? input.assistant : '',
    agenticIvr: typeof input.agenticIvr === 'string' ? input.agenticIvr : '',
    agents: typeof input.agents === 'string' ? input.agents : '',
    vendorBackoffice: typeof input.vendorBackoffice === 'string' ? input.vendorBackoffice : ''
  };
};

const formatSubscriptionLabel = (value) => {
  if (typeof value !== 'string') {
    return '';
  }
  const normalized = value.trim().toLowerCase();
  if (normalized === 'standard') {
    return 'Standard';
  }
  if (normalized === 'enterprise') {
    return 'Enterprise';
  }
  if (normalized === '') {
    return '';
  }
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const loadSettings = async () => {
  isLoading.value = true;
  loadingError.value = null;
  try {
    const response = await axios.get(`${apiEndpoint}/api/organizationSettings`);
    const responseData = response.data;
    const timezoneValue = isDefinedAndNotEmpty(responseData.timezone) === true ? [responseData.timezone] : [];
    const languageValue = isDefinedAndNotEmpty(responseData.language) === true ? responseData.language : 'en';
    const logoValue = isDefinedAndNotEmpty(responseData.logoUrl) === true ? String(responseData.logoUrl).trim() : '';
    const allowedIpsValue = isDefinedAndNotEmpty(responseData.allowedIps) === true ? String(responseData.allowedIps).trim() : '';
    ipList.value = allowedIpsValue !== '' ? allowedIpsValue.split(',').map(i => i.trim()).filter(i => i !== '') : [];
    settings.value = {
      organizationName: isDefinedAndNotEmpty(responseData.organizationName) === true ? responseData.organizationName : '',
      subscription: isDefinedAndNotEmpty(responseData.subscription) === true ? responseData.subscription : '',
      subscriptionConfig: typeof responseData.subscriptionConfig === 'object' && responseData.subscriptionConfig !== null ? responseData.subscriptionConfig : null,
      timezone: timezoneValue,
      language: languageValue,
      logoUrl: logoValue,
      allowedIps: allowedIpsValue,
      customDomains: normalizeCustomDomains(responseData.customDomains)
    };
    if (logoFileInput.value !== null && logoFileInput.value !== undefined) {
      logoFileInput.value.value = '';
    }
    updateWhitelabelLogo(logoValue);
  } catch (error) {
    log.error('Error loading organization settings:', error);
    loadingError.value = isDefinedAndNotEmpty(error.response?.data?.error) === true ? error.response.data.error : (isDefinedAndNotEmpty(error.message) === true ? error.message : 'Unknown error');
    toast.error('Failed to load organization settings');
  } finally {
    isLoading.value = false;
  }
};

const saveSettings = async () => {
  isSaving.value = true;
  try {
    settings.value.allowedIps = ipList.value.join(',');
    const preparedLogo = isDefinedAndNotEmpty(settings.value.logoUrl) === true ? String(settings.value.logoUrl).trim() : null;
    const response = await axios.put(`${apiEndpoint}/api/organizationSettings`, {
        organizationName: settings.value.organizationName,
        timezone: settings.value.timezone.length > 0 ? settings.value.timezone[0] : null,
        language: settings.value.language,
        logoUrl: preparedLogo,
        allowedIps: settings.value.allowedIps,
        customDomains: normalizeCustomDomains(settings.value.customDomains)
    });
    const responseData = response.data;
    const timezoneValue = isDefinedAndNotEmpty(responseData.timezone) === true ? [responseData.timezone] : [];
    const languageValue = isDefinedAndNotEmpty(responseData.language) === true ? responseData.language : 'en';
    const logoValue = isDefinedAndNotEmpty(responseData.logoUrl) === true ? String(responseData.logoUrl).trim() : '';
    const allowedIpsValue = isDefinedAndNotEmpty(responseData.allowedIps) === true ? String(responseData.allowedIps).trim() : '';
    ipList.value = allowedIpsValue !== '' ? allowedIpsValue.split(',').map(i => i.trim()).filter(i => i !== '') : [];
    settings.value = {
      organizationName: isDefinedAndNotEmpty(responseData.organizationName) === true ? responseData.organizationName : '',
      subscription: isDefinedAndNotEmpty(responseData.subscription) === true ? responseData.subscription : '',
      subscriptionConfig: typeof responseData.subscriptionConfig === 'object' && responseData.subscriptionConfig !== null ? responseData.subscriptionConfig : null,
      timezone: timezoneValue,
      language: languageValue,
      logoUrl: logoValue,
      allowedIps: allowedIpsValue,
      customDomains: normalizeCustomDomains(responseData.customDomains)
    };
    if (logoFileInput.value !== null && logoFileInput.value !== undefined) {
      logoFileInput.value.value = '';
    }
    updateWhitelabelLogo(logoValue);
    toast.success('Organization settings saved successfully!');
    } catch (error) {
    log.error('Error saving organization settings:', error);
    toast.error(isDefinedAndNotEmpty(error.response?.data?.error) === true ? error.response.data.error : (isDefinedAndNotEmpty(error.message) === true ? error.message : 'Failed to save settings'));
  } finally {
    isSaving.value = false;
  }
};

const onLogoFileChange = (event) => {
  const target = event !== undefined && event !== null ? event.target : null;
  const files = target !== null && target !== undefined ? target.files : null;
  const file = files !== null && files !== undefined && files.length > 0 ? files[0] : null;
  if (file !== null) {
    const fileType = typeof file.type === 'string' ? file.type : '';
    if (fileType.startsWith('image/') === true) {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result === 'string' && result !== '') {
          settings.value.logoUrl = result;
        }
      };
      reader.readAsDataURL(file);
    } else {
      if (target !== null && target !== undefined) {
        target.value = '';
      }
      toast.error('Please select an image file');
    }
  }
};

const clearLogo = () => {
  settings.value.logoUrl = '';
  if (logoFileInput.value !== null && logoFileInput.value !== undefined) {
    logoFileInput.value.value = '';
  }
};

const onLogoUrlBlur = (event) => {
  const target = event !== undefined && event !== null ? event.target : null;
  const value = target !== null && target !== undefined ? target.value : '';
  if (typeof value === 'string') {
    settings.value.logoUrl = value.trim();
  }
};

const addIp = () => {
  const ip = newIp.value.trim();
  if (ip !== '' && ipList.value.includes(ip) === false) {
    ipList.value.push(ip);
  }
  newIp.value = '';
};

const removeIp = (index) => {
  ipList.value.splice(index, 1);
};

onMounted(() => {
  loadSettings();
});

</script>

<style scoped>
.settings-container {
  max-width: 100%;
  margin: 0 auto;

}

.multiselect-custom {
  width: 100%;

}

.dark .multiselect-custom {

}

.tab-content {
  min-height: 300px;
}
</style>
